import { flexRender, getCoreRowModel, useReactTable, type ColumnDef, type RowData } from '@tanstack/react-table'
import { ArrowDown, ArrowUp, ChevronsUpDown } from 'lucide-react'
import type { ReactNode } from 'react'
import type { SortDirection } from '@/types/api'
import { cn } from '@/lib/cn'
import { EmptyState, ErrorState } from './States'
import { TableSkeleton } from './Skeleton'
import styles from './DataTable.module.css'

declare module '@tanstack/react-table' {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  interface ColumnMeta<TData extends RowData, TValue> {
    /** Server sort key. A column without one is not sortable. */
    sortKey?: string
    /** Label shown beside the value in the stacked mobile layout. */
    mobileLabel?: string
    /** Drops the column below the mobile breakpoint. */
    hideOnMobile?: boolean
    align?: 'left' | 'right' | 'center'
    /** Pins the column to the right edge — used for the actions column. */
    sticky?: boolean
    width?: string
  }
}

export interface DataTableProps<T> {
  data: T[]
  columns: ColumnDef<T, unknown>[]
  getRowId: (row: T) => string
  isLoading?: boolean
  /** True while a background refetch runs — dims rather than blanks the table. */
  isFetching?: boolean
  error?: unknown
  onRetry?: () => void
  sort?: string
  direction?: SortDirection
  onSortChange?: (sortKey: string, direction: SortDirection) => void
  emptyTitle?: string
  emptyDescription?: string
  emptyAction?: ReactNode
  /** Accessible name for the table. */
  caption: string
  className?: string
}

export function DataTable<T>({
  data,
  columns,
  getRowId,
  isLoading = false,
  isFetching = false,
  error,
  onRetry,
  sort,
  direction = 'desc',
  onSortChange,
  emptyTitle = 'Nothing to show',
  emptyDescription,
  emptyAction,
  caption,
  className,
}: DataTableProps<T>) {
  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getRowId,
    manualSorting: true,
    manualPagination: true,
    manualFiltering: true,
  })

  if (error) {
    return <ErrorState error={error} onRetry={onRetry} />
  }

  if (isLoading) {
    return <TableSkeleton rows={8} columns={Math.min(columns.length, 7)} />
  }

  if (data.length === 0) {
    return <EmptyState title={emptyTitle} description={emptyDescription} action={emptyAction} />
  }

  return (
    <div className={cn(styles.scroller, isFetching && styles.refreshing, className)} data-print="area">
      <table className={styles.table}>
        <caption className="visually-hidden">{caption}</caption>
        <thead className={styles.head}>
          {table.getHeaderGroups().map((headerGroup) => (
            <tr key={headerGroup.id}>
              {headerGroup.headers.map((header) => {
                const meta = header.column.columnDef.meta
                const sortKey = meta?.sortKey
                const isSorted = Boolean(sortKey) && sortKey === sort
                const nextDirection: SortDirection = isSorted && direction === 'desc' ? 'asc' : 'desc'

                return (
                  <th
                    key={header.id}
                    scope="col"
                    style={{ width: meta?.width }}
                    className={cn(
                      meta?.align === 'right' && styles.alignRight,
                      meta?.align === 'center' && styles.alignCenter,
                      meta?.hideOnMobile && styles.hideOnMobile,
                      meta?.sticky && styles.stickyColumn,
                    )}
                    aria-sort={isSorted ? (direction === 'asc' ? 'ascending' : 'descending') : undefined}
                  >
                    {sortKey && onSortChange ? (
                      <button
                        type="button"
                        className={cn(styles.sortButton, isSorted && styles.sorted)}
                        onClick={() => onSortChange(sortKey, nextDirection)}
                      >
                        {flexRender(header.column.columnDef.header, header.getContext())}
                        {isSorted ? (
                          direction === 'asc' ? (
                            <ArrowUp aria-hidden="true" />
                          ) : (
                            <ArrowDown aria-hidden="true" />
                          )
                        ) : (
                          <ChevronsUpDown className={styles.sortIdle} aria-hidden="true" />
                        )}
                      </button>
                    ) : (
                      flexRender(header.column.columnDef.header, header.getContext())
                    )}
                  </th>
                )
              })}
            </tr>
          ))}
        </thead>

        <tbody>
          {table.getRowModel().rows.map((row) => (
            <tr key={row.id} className={styles.row}>
              {row.getVisibleCells().map((cell) => {
                const meta = cell.column.columnDef.meta
                return (
                  <td
                    key={cell.id}
                    // Drives the "Label: value" layout on narrow screens.
                    data-label={meta?.mobileLabel}
                    className={cn(
                      meta?.align === 'right' && styles.alignRight,
                      meta?.align === 'center' && styles.alignCenter,
                      meta?.hideOnMobile && styles.hideOnMobile,
                      meta?.sticky && styles.stickyColumn,
                    )}
                  >
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </td>
                )
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}
